import * as React from "react";
import {
  Autocomplete,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Grid,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import {
  GridColDef,
  GridRenderCellParams,
  GridValueGetterParams,
} from "@mui/x-data-grid";
import {
  useGetCategoriesQuery,
  useGetProductByIdMutation,
  useSyncNewArrivalProductMutation,
  useGetNewArrivalProductsMutation,
  useRemoveAggregateProductAsyncMutation,
  useRemoveBulkAggregateProductsMutation,
} from "../../redux/reducer/features/Product/productSlice";
import { Product } from "../../models/features/Product/Product";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ServerAppGrid from "../app/ServerAppGrid";
import { AppFilter, AppFilterItem } from "../../models/app/AppModels";
import ProductDetail from "../Integration/DmsSetup/Popup/ProductDetail";
import { AppState } from "../../redux/reducer/rootReducer";
import { useSelector } from "react-redux";
import { AppGridId } from "../../models/app/AppGridModels";
import {
  GetAppGridColumns,
  getProductRedirectUrl,
} from "../../services/utilities/utility";
import TruncatedCellWithTooltip from "../common/TruncatedCellWithTooltip";
import { useGetDMSManufacturersBySettingsIdQuery } from "../../redux/reducer/features/Integration/DmsSetupSlice";
import { SetupType } from "../../models/features/Integration/DmsSetup";

const NewProducts: React.FunctionComponent = () => {
  const navigate = useNavigate();
  const gridSetting = useSelector((state: AppState) =>
    state?.AppGridReducer?.setting.find(
      (x) => x.gridId === AppGridId.New_Products,
    ),
  );
  const { dmsSetup } = useSelector((state: AppState) => state.authReducer);
  const {
    data: mappedManufacturers,
    isSuccess,
    isLoading: MfrsLoading,
  } = useGetDMSManufacturersBySettingsIdQuery({
    dealerSettingsId: dmsSetup?.dealerSettings?.id ?? "",
  });
  const [paging, setPaging] = React.useState<AppFilter>({
    pageIndex: 0,
    pageSize: 10,
    totalRecords: 0,
    items: [],
    quickFilterValues: [],
    virtualShowroomProducts: true,
  });
  const [pagingItems, setPagingItems] = React.useState<AppFilterItem[]>([]);
  const { data, isLoading } = useGetCategoriesQuery();
  const [selectedCategory, setSelectedCategory] = React.useState("");
  const [selectedManufacturer, setSelectedManufacturer] = React.useState("");
  const [products, setProducts] = React.useState<any[]>([]);
  const [Formopen, setFormOpen] = React.useState(false);
  const [selectedProduct, setSelectedProduct] = React.useState<Product>();
  const [productsCount, setProductsCount] = React.useState<number>(0);
  const [productsData, productsDataResult] = useGetNewArrivalProductsMutation();
  const [productByIdMutation, productByIdMutationResult] =
    useGetProductByIdMutation();
  const [syncNewArrivalMutation, syncNewArrivalMutationResult] =
    useSyncNewArrivalProductMutation();
  const [removeNewArrivalMutation, removeNewArrivalMutationResult] =
    useRemoveBulkAggregateProductsMutation();

  // Redirect BigCommerce users away from Virtual Showroom page
  React.useEffect(() => {
    if (dmsSetup?.setupType === SetupType.BigCommerce) {
      navigate("/");
    }
  }, [dmsSetup?.setupType, navigate]);

  const categoryDefaultProps = {
    options:
      data && data.entityList
        ? data.entityList.filter((n) => !n.isSubCategory)
        : [],
    getOptionLabel: (option: any) => option.title,
  };

  const manufacturerDefaultProps = {
    options:
      mappedManufacturers && mappedManufacturers.entityList
        ? mappedManufacturers.entityList
        : [],
    getOptionLabel: (option: any) => option.catalogManufacturer.title,
  };
  const handleSyncNewArrival = (id: string) => {
    syncNewArrivalMutation({ productId: id }).then((res: any) => {
      if (res.data.status === 200) {
        fetchData();
      }
    });
  };

  const handleRemoveNewArrival = (id: string) => {
    removeNewArrivalMutation({ ProductAggregateIds: [id] }).then((res: any) => {
      if (res.data.status === 200) {
        fetchData();
      }
    });
  };
  const columns: GridColDef[] = [
    {
      field: "catalogProduct.manufacturer",
      headerName: "Manufacturer",
      flex: 1,
      filterable: false,
      renderCell: (params: GridRenderCellParams<any>) => {
        const manufacturer =
          params.row.catalogProduct.productManufacturer.title;
        return <TruncatedCellWithTooltip value={manufacturer} />;
      },
    },
    {
      field: "modelNumber",
      headerName: "Model",
      flex: 1,
      renderCell: (params: GridRenderCellParams<any>) => {
        return (
          <TruncatedCellWithTooltip
            value={params.row.catalogProduct.modelNumber}
          />
        );
      },
    },
    {
      field: "title",
      headerName: "Title",
      flex: 1,
      renderCell: (params: GridRenderCellParams<any>) => {
        return (
          <TruncatedCellWithTooltip value={params.row.catalogProduct.title} />
        );
      },
    },
    {
      field: "catalogProduct.categoryTitle",
      headerName: "Category",
      flex: 1,
      filterable: false,
      renderCell: (params: GridRenderCellParams<any>) => {
        return (
          <TruncatedCellWithTooltip
            value={params.row.catalogProduct.category.title}
          />
        );
      },
    },
    {
      field: "actions",
      type: "actions",
      headerName: "Catalog Details",
      flex: 1,
      renderCell: (params: GridRenderCellParams) => {
        return (
          <div>
            <IconButton
              size="small"
              id="basic-button"
              onClick={(e) => {
                setFormOpen(true);
                productByIdMutation({
                  data: { id: params.row.catalogProduct.id },
                });
              }}
            >
              <VisibilityIcon />
            </IconButton>
          </div>
        );
      },
    },
    {
      field: "synceType",
      headerName: "Sync Type",
      type: "actions",
      flex: 1,
      description:
        "Wholegood(Synced as wholegood into shopify),\n Parts(Synced as Part into shopify),\n Used Product(Synced as Used Product into shopify),  Virtual Showroom(Synced as Virtual Showroom into shopify)",
      renderCell: (params: GridRenderCellParams) => {
        return getSyncType(params?.row?.externalProduct);
      },
    },
    {
      field: "actions1",
      headerName: "Shopify Status",
      type: "actions",
      flex: 1,
      renderCell: (params: GridRenderCellParams) => {
        return params.row?.isSynced !== true ? (
          <Chip size="small" label="Not Synced" color="primary" />
        ) : (
          <Chip
            label="View"
            size="small"
            color="success"
            onClick={() => {
              window.open(
                getProductRedirectUrl(
                  dmsSetup?.siteDomain ?? "",
                  params.row?.externalProduct?.handle,
                  dmsSetup?.setupType ?? SetupType.Shopify,
                ),
                "_blank",
              );
            }}
          />
        );
      },
    },
    {
      field: "Sync",
      headerName: "Sync To Virtual Showroom",
      type: "actions",
      flex: 1,
      renderCell: (params: GridRenderCellParams) => {
        return params.row.isSynced === true ? (
          <Tooltip title={getDeleteTooltip(params.row.externalProduct)}>
            <span>
              <Button
                disabled={
                  removeNewArrivalMutationResult.isLoading ||
                  params.row.externalProduct.type !== "NewArrival"
                }
                color="error"
                variant="contained"
                size="small"
                sx={{ fontSize: "0.7rem" }}
                onClick={() => {
                  handleRemoveNewArrival(
                    params.row.externalProduct.aggregateId,
                  );
                }}
              >
                Delete
              </Button>
            </span>
          </Tooltip>
        ) : (
          <Button
            disabled={syncNewArrivalMutationResult.isLoading}
            color="warning"
            variant="contained"
            size="small"
            onClick={() => {
              handleSyncNewArrival(params.row.catalogProduct.id);
            }}
          >
            Sync
          </Button>
        );
      },
    },
  ];

  const fetchData = () => {
    productsData({
      paging: {
        ...paging,
        sectionOnly: false,
        manufacturerIds: mappedManufacturers?.entityList
          ? mappedManufacturers.entityList.map(
              (mfr: any) => mfr.catalogManufacturerId,
            )
          : [],
        items: [...pagingItems, ...paging.items],
      },
    });
  };

  React.useEffect(() => {
    if (isSuccess) {
      fetchData();
    }
  }, [paging, pagingItems, isSuccess]);
  React.useEffect(() => {
    if (productByIdMutationResult.isSuccess) {
      setSelectedProduct(productByIdMutationResult.data.entity);
    }
  }, [productByIdMutationResult]);
  React.useEffect(() => {
    if (productsDataResult.data) {
      setProducts(productsDataResult.data.entityList);
      setProductsCount(productsDataResult.data.totalRecords);
    }
  }, [productsDataResult]);

  React.useEffect(() => {
    var categoryTitle = data?.entityList?.find(
      (n) => n.id === selectedCategory,
    );

    if (categoryTitle) {
      var items: AppFilterItem[] = pagingItems;

      var itemToAdd: AppFilterItem = {
        columnField: "categoryTitle",
        operatorValue: "equals",
        value: categoryTitle?.title ?? "",
      };

      if (pagingItems.some((n) => n.columnField === "categoryTitle")) {
        items = items.map((n) =>
          n.columnField === "categoryTitle" ? itemToAdd : n,
        );
        setPagingItems(items);
      } else {
        setPagingItems([...pagingItems, itemToAdd]);
      }
    } else {
      setPagingItems([
        ...pagingItems.filter((n) => n.columnField !== "categoryTitle"),
      ]);
    }
  }, [selectedCategory]);

  React.useEffect(() => {
    var manufacturer = mappedManufacturers?.entityList?.find(
      (n) => n.id === selectedManufacturer,
    );
    if (manufacturer) {
      var items: AppFilterItem[] = pagingItems;

      var itemToAdd: AppFilterItem = {
        columnField: "manufacturer",
        operatorValue: "equals",
        value: manufacturer?.catalogManufacturer?.title ?? "",
      };

      if (pagingItems.some((n) => n.columnField === "manufacturer")) {
        items = items.map((n) =>
          n.columnField === "manufacturer" ? itemToAdd : n,
        );
        setPagingItems(items);
      } else {
        setPagingItems([...pagingItems, itemToAdd]);
      }
    } else {
      setPagingItems([
        ...pagingItems.filter((n) => n.columnField !== "manufacturer"),
      ]);
    }
  }, [selectedManufacturer]);
  const handleClose = () => {
    setFormOpen(false);
  };
  return (
    <>
      <Container maxWidth={false}>
        <Typography variant="h6" sx={{ fontWeight: "bold" }} gutterBottom>
          Virtual Showroom
        </Typography>
        <Card>
          <CardContent sx={{ paddingTop: 0, pb: 0 }}>
            <Grid
              container
              spacing={2}
              mt={0.3}
              justifyContent="space-between"
              display="flex"
            >
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  {...manufacturerDefaultProps}
                  id="manufacturersList"
                  clearOnEscape
                  loading={isLoading}
                  onChange={(e, value) => {
                    setSelectedManufacturer(value?.id ?? "");
                  }}
                  renderOption={(props, option) => (
                    <li {...props} key={option.id}>
                      {option.catalogManufacturer.title}
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Manufacturer"
                      variant="standard"
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  {...categoryDefaultProps}
                  id="categoriesList"
                  loading={isLoading}
                  clearOnEscape
                  onChange={(e, value) => {
                    setSelectedCategory(value?.id ?? "");
                  }}
                  renderOption={(props, option) => (
                    <li {...props} key={option.id}>
                      {option.title}
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Category"
                      variant="standard"
                    />
                  )}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
        <Grid
          container
          spacing={3}
          justifyContent="space-between"
          display="flex"
          pt={1}
        >
          <Grid item md={12}>
            <ServerAppGrid
              gridId={AppGridId.New_Products}
              data={
                products && products?.length > 0
                  ? products.map((n, i) => ({ id: i, ...n }))
                  : []
              }
              columns={GetAppGridColumns(
                columns,
                gridSetting?.columnVisibilityModel,
              )}
              isLoading={
                productsDataResult.isLoading ||
                syncNewArrivalMutationResult.isLoading ||
                removeNewArrivalMutationResult.isLoading ||
                MfrsLoading
              }
              paging={paging}
              setPaging={setPaging}
              totalRecords={productsCount}
              height={"68vh"}
              hideCheckBoxes={true}
            />
          </Grid>
        </Grid>
        <ProductDetail
          isOpen={Formopen}
          handleClose={handleClose}
          productData={selectedProduct}
          Loading={productByIdMutationResult.isLoading}
        />
      </Container>
    </>
  );
};

export default NewProducts;

const getSyncType = (externalProduct: any) => {
  switch (externalProduct?.type) {
    case "Wholegoods":
      return <Chip size="small" label="Wholegoods" color="primary" />;

    case "Parts":
      return <Chip size="small" label="Parts" color="primary" />;

    case "NewArrival":
      return <Chip size="small" label="Virtual Showroom" color="primary" />;

    case "UsedProduct":
      return <Chip size="small" label="Used Product" color="primary" />;

    default:
      return <Chip size="small" label="N/A" color="warning" />;
  }
};

const getDeleteTooltip = (externalProduct: any) => {
  switch (externalProduct?.type) {
    case "Wholegoods":
    case "Parts":
      return "This product can be deleted from the Product Manager Section";
    case "NewArrival":
      return "Delete this product from Shopify";
    case "UsedProduct":
      return "This product can be deleted from the Used Product Section";
    default:
      return "";
  }
};
